<template>
  <div>
    <div v-if="errorMessages.length > 0" class="datei-upload-errors">
      <div v-for="(msg, index) in errorMessages" :key="index" class="datei-upload-errors__item">
        {{ msg }}
      </div>
    </div>
    <div
      v-if="showDropzone"
      class="datei-upload-dropzone"
      :class="{
        'datei-upload-dropzone--active': isDragging,
      }"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
      @click="openFileDialog"
    >
      <input
        ref="fileInput"
        type="file"
        :accept="acceptFormatsString"
        :multiple="allowMultipleFiles"
        class="d-none"
        @change="onFileSelected"
      />
      <div class="datei-upload-dropzone__content">
        <FileUp :size="24" style="color: var(--primary)" />
        <span class="datei-upload-dropzone__link">Dateien hochladen</span>
        <span class="datei-upload-dropzone__hint">oder Dateien hier hinziehen</span>
      </div>
    </div>

    <div v-if="files.length > 0" class="datei-upload-files" :class="{ 'mt-2': showDropzone }">
      <div v-for="(file, index) in files" :key="index" class="datei-upload-files__row">
        <div class="datei-upload-files__item">
          <div class="d-flex align-items-center" style="min-width: 0">
            <component
              :is="getFileIcon(file.name)"
              :size="16"
              class="mr-2 flex-shrink-0"
              style="color: var(--primary)"
            />
            <span class="datei-upload-files__name">{{ file.name }}</span>
            <span class="datei-upload-files__size ml-2">({{ formatFileSize(file.size) }})</span>
          </div>
        </div>
        <X :size="14" class="text-danger datei-upload-files__remove" @click.stop="removeFile(index)" />
      </div>
    </div>
  </div>
</template>

<script>
import { FileUp, FileText, Image, FileSpreadsheet, File, X } from 'lucide-vue';
import { formatFileSize, getFileIcon } from '@/core/common/helpers/datei.utils';

const DEFAULT_ACCEPT_FORMATS = ['.txt', '.pdf', '.docx', '.jpeg', '.png', '.xlsx', '.xls', '.csv'];

export default {
  components: { FileUp, FileText, ImageIcon: Image, FileSpreadsheet, File, X },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    acceptFormats: {
      type: Array,
      default: () => DEFAULT_ACCEPT_FORMATS,
    },
    allowMultipleFiles: {
      type: Boolean,
      default: true,
    },
    maxFileSizeMb: {
      type: Number,
      default: 10,
    },
    // Enthält Namen von Dateien, die bereits serverseitig vorhanden sind und nicht im v-model stehen
    // Wird für die Duplikats-Prüfung herangezogen, z B bei bereits hochgeladenen Anhängen einer Aufgabe
    existingFileNames: {
      type: Array,
      default: () => [],
    },
    // Blendet die Dropzone aus, sobald eine Datei ausgewählt ist
    hideDropzoneWithFiles: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDragging: false,
      errorMessages: [],
      errorTimeoutId: null,
    };
  },
  beforeDestroy() {
    if (this.errorTimeoutId) clearTimeout(this.errorTimeoutId);
  },
  computed: {
    files() {
      return this.value;
    },
    showDropzone() {
      return !this.hideDropzoneWithFiles || this.files.length === 0;
    },
    acceptFormatsString() {
      return this.acceptFormats.join(',');
    },
  },
  methods: {
    formatFileSize,
    getFileIcon,
    openFileDialog() {
      this.$refs.fileInput.click();
    },
    onDragOver() {
      this.isDragging = true;
    },
    onDragLeave() {
      this.isDragging = false;
    },
    onDrop(event) {
      this.isDragging = false;
      const droppedFiles = Array.from(event.dataTransfer.files);
      this.addFiles(droppedFiles);
    },
    onFileSelected(event) {
      const selectedFiles = Array.from(event.target.files);
      this.addFiles(selectedFiles);
      this.$refs.fileInput.value = '';
    },
    addFiles(newFiles) {
      // Vereinigt bereits ausgewählte Dateien mit den über das Prop gemeldeten Dateinamen
      // Dadurch werden sowohl clientseitige als auch bereits serverseitige Duplikate erkannt
      const existingNames = new Set([...this.files.map(f => f.name), ...this.existingFileNames]);
      const validFiles = [];
      for (const file of newFiles) {
        if (!this.validateFile(file)) continue;
        if (existingNames.has(file.name)) {
          const message = `Die Datei: "${file.name}" ist bereits vorhanden.`;
          this.showError(message);
          this.$emit('error', { file, type: 'duplicate', message });
          continue;
        }
        existingNames.add(file.name);
        validFiles.push(file);
      }
      if (validFiles.length === 0) return;

      const updatedFiles = this.allowMultipleFiles ? [...this.files, ...validFiles] : [validFiles[0]];
      this.$emit('input', updatedFiles);
    },
    validateFile(file) {
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      if (!this.acceptFormats.includes(extension)) {
        const message = `Das Format der Datei: "${
          file.name
        }" ist nicht zulässig. Zulässige Formate: ${this.acceptFormats.join(', ')}`;
        this.showError(message);
        this.$emit('error', { file, type: 'format', message });
        return false;
      }
      if (file.size > this.maxFileSizeMb * 1024 * 1024) {
        const message = `"${file.name}" ist zu groß. Die Datei darf maximal ${this.maxFileSizeMb} MB groß sein.`;
        this.showError(message);
        this.$emit('error', { file, type: 'size', message });
        return false;
      }
      return true;
    },
    showError(message) {
      this.errorMessages.push(message);
      if (this.errorTimeoutId) clearTimeout(this.errorTimeoutId);
      this.errorTimeoutId = setTimeout(() => {
        this.errorMessages = [];
        this.errorTimeoutId = null;
      }, 5000);
    },
    removeFile(index) {
      const updatedFiles = this.files.filter((_, i) => i !== index);
      this.$emit('input', updatedFiles);
    },
  },
};
</script>

<style scoped>
.datei-upload-errors {
  margin-bottom: 6px;
}

.datei-upload-errors__item {
  color: var(--danger);
  font-size: 13px;
  line-height: 1.3;
}

.datei-upload-dropzone {
  border: 1.6px solid var(--border);
  border-radius: var(--radius-md);
  padding: 4px 24px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
  background-color: var(--surface-hover);
}

.datei-upload-dropzone:hover {
  border-color: var(--primary);
}

.datei-upload-dropzone--active {
  border-color: var(--primary);
}

.datei-upload-dropzone__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.datei-upload-dropzone__link {
  color: var(--primary);
  font-weight: 500;
  font-size: 14px;
}

.datei-upload-dropzone__hint {
  color: #6b7280;
  font-size: 13px;
}

.datei-upload-files__row {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  position: relative;
}

.datei-upload-files__item {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  padding: 6px 10px;
  border: 1px solid var(--primary);
  border-radius: var(--radius-sm);
  background: var(--surface);
}

.datei-upload-files__name {
  font-size: 13px;
  color: var(--text-body);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.datei-upload-files__size {
  font-size: 12px;
  color: #9ca3af;
  flex-shrink: 0;
}

.datei-upload-files__remove {
  cursor: pointer;
  flex-shrink: 0;
  width: 14px;
  margin-left: 8px;
  position: absolute;
  right: -22px;
}
</style>
